import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import type {
  CreatorRequest,
  CreatorRequestComment,
  CreatorRequestDetail,
  CreatorRequestCancellationReason,
  CreatorRequestInviteAttachment,
  CreatorRequestRequester,
  CreatorRequestStatus,
} from '@/api/types';

const KEYS = {
  all: ['inviter', 'creator-requests'] as const,
  list: (params?: InviterCreatorRequestsParams) => [...KEYS.all, 'list', params] as const,
  detail: (publicId: string) => [...KEYS.all, 'detail', publicId] as const,
  invites: (publicId: string) => [...KEYS.all, 'detail', publicId, 'invites'] as const,
  requesters: [...['inviter', 'creator-requests'], 'requesters'] as const,
};

export interface InviterCreatorRequestsParams {
  [key: string]: string | number | boolean | undefined;
  status?: string; // comma-separated CreatorRequestStatus list
  niche?: string;
  channel?: string;
  requesterExternalId?: string;
  requesterName?: string;
  assignedInviterId?: string | number;
  page?: number;
  limit?: number;
}

export function useInviterCreatorRequests(params: InviterCreatorRequestsParams = {}) {
  const api = useApi();
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () => api.getPaged<CreatorRequest[]>('/portal/creator-requests', params),
  });
}

export function useInviterCreatorRequest(publicId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: publicId ? KEYS.detail(publicId) : KEYS.all,
    enabled: !!publicId,
    queryFn: () => api.get<CreatorRequestDetail>(`/portal/creator-requests/${publicId}`),
  });
}

export interface UpdateInviterCreatorRequestBody {
  status?: CreatorRequestStatus;
  cancellationReason?: CreatorRequestCancellationReason | null;
  cancellationNote?: string | null;
  assignedInviterId?: number | null;
  rosterInviteId?: number | null;
}

export function useUpdateInviterCreatorRequest() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      body,
    }: {
      publicId: string;
      body: UpdateInviterCreatorRequestBody;
    }) =>
      api.patch<CreatorRequest>(`/portal/creator-requests/${publicId}`, body),
    // Optimistic update — write the new status/assignment into the detail cache
    // before the network call resolves so the stepper snaps to the new state
    // instantly. On error we roll back to the snapshot captured in onMutate.
    onMutate: async (vars) => {
      const detailKey = KEYS.detail(vars.publicId);
      await qc.cancelQueries({ queryKey: detailKey });
      const previous = qc.getQueryData<CreatorRequestDetail>(detailKey);
      if (previous) {
        const next: CreatorRequestDetail = { ...previous };
        if (vars.body.status !== undefined) next.status = vars.body.status;
        if (vars.body.cancellationReason !== undefined) {
          next.cancellationReason = vars.body.cancellationReason ?? null;
        }
        if (vars.body.cancellationNote !== undefined) {
          next.cancellationNote = vars.body.cancellationNote ?? null;
        }
        if (vars.body.assignedInviterId !== undefined) {
          next.assignedInviterId = vars.body.assignedInviterId ?? null;
        }
        if (vars.body.rosterInviteId !== undefined) {
          next.rosterInviteId = vars.body.rosterInviteId ?? null;
        }
        qc.setQueryData(detailKey, next);
      }
      return { previous };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(KEYS.detail(vars.publicId), ctx.previous);
      }
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.publicId) });
    },
  });
}

export function useAddInviterCreatorRequestComment() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, body }: { publicId: string; body: string }) =>
      api.post<CreatorRequestComment>(`/portal/creator-requests/${publicId}/comments`, { body }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.publicId) });
    },
  });
}

export function useInviterRequesterDirectory(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: KEYS.requesters,
    enabled,
    queryFn: () =>
      api.get<CreatorRequestRequester[]>('/portal/creator-requests/requesters'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useInviterRequestInvites(publicId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: publicId ? KEYS.invites(publicId) : KEYS.all,
    enabled: !!publicId,
    queryFn: () =>
      api.get<CreatorRequestInviteAttachment[]>(
        `/portal/creator-requests/${publicId}/invites`,
      ),
  });
}

export function useAttachInviteToRequest() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, inviteId }: { publicId: string; inviteId: number }) =>
      api.post<{ inviteId: number; invitePublicId: string; attachedCount: number }>(
        `/portal/creator-requests/${publicId}/invites`,
        { inviteId },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.invites(vars.publicId) });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.publicId) });
    },
  });
}

export function useDetachInviteFromRequest() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, inviteId }: { publicId: string; inviteId: number }) =>
      api.del<{ attachedCount: number }>(
        `/portal/creator-requests/${publicId}/invites/${inviteId}`,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.invites(vars.publicId) });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.publicId) });
    },
  });
}
